import React from 'react';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import GlassButton from '../../components/Aero/GlassButton';
import AeroIcon from '../../components/Aero/AeroIcon';
import { AeroStatus } from '../../components/Aero/AeroHomeParts';
import AeroCardMenu from '../../components/Aero/AeroCardMenu';

/**
 * The top of a site's page (AeroSiteDetail), v3: the 52px header bar with
 * "Sites / name" and Copy address and Visit; then the editable title with its
 * status and the address. The sections below it are one page (SiteOnePage). A view: state and handlers stay in
 * AeroSiteDetail.
 *
 * Demo parity: a ⋯ after Visit holding "Test this site" (or "Open its test"),
 * `menuItems` from AeroSiteDetail (testThisSite.testMenuItem). No ⋯ without items.
 */
export default function SiteDetailHead({
  site, isLive, isShared, access = null, onUpgrade = null, liveUrl, liveHost, copied, onCopy, onBack,
  editingTitle, titleDraft, savingTitle, onTitleDraft, onStartTitleEdit, onSaveTitle, onTitleKeyDown,
  menuItems = [],
}) {
  const name = site.title || site.generatedUrl;
  // A live site on an ended Free plan is offline for visitors (utils/siteAccess.js).
  const offline = isLive && access?.state === 'expired';
  return (
    <>
      <AeroPageHeader
        crumbs={[{ label: 'Sites', onClick: onBack }, { label: name }]}
        actions={(
          <>
            <GlassButton fill="glass" size="sm" onClick={onCopy} aria-label={copied ? 'Copied' : 'Copy address'}>
              <AeroIcon name="copy" />
              <span className="aero-ph-label">{copied ? 'Copied' : 'Copy address'}</span>
            </GlassButton>
            <GlassButton
              fill="glass"
              size="sm"
              aria-label="Visit"
              onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}
            >
              <AeroIcon name="ext" />
              <span className="aero-ph-label">Visit</span>
            </GlassButton>
            {menuItems.length > 0 && <AeroCardMenu items={menuItems} className="aero-ph-menu" />}
          </>
        )}
      />

      <div className="aero-sd-top">
        {editingTitle ? (
          <input
            type="text"
            className="aero-input aero-sd-title-edit"
            value={titleDraft}
            autoFocus
            disabled={savingTitle}
            onChange={(e) => onTitleDraft(e.target.value)}
            onBlur={onSaveTitle}
            onKeyDown={onTitleKeyDown}
            aria-label="Site name"
          />
        ) : (
          <h2 className="aero-sd-title" onClick={onStartTitleEdit} title="Click to rename">
            {name}
          </h2>
        )}
        <div className="aero-sd-addr">
          <span className="aero-sd-host">{liveHost}</span>
          {offline
            ? <AeroStatus kind="off">Offline: free plan ended</AeroStatus>
            : <AeroStatus kind={isLive ? 'live' : 'off'} />}
          {offline && onUpgrade && (
            <GlassButton fill="ink" size="xs" onClick={onUpgrade}>Upgrade</GlassButton>
          )}
          {site.isPasswordActive && <AeroStatus kind="priv" />}
          {isShared && <span className="aero-smp" title="Shared with you by an admin">Shared</span>}
        </div>
      </div>

    </>
  );
}
